import React from "react";
import { Flex, Text } from "styles/utility-components";

import Checkbox from "components/ui/checkbox/Checkbox";
import Divider from "components/ui/divider/Divider";
import { FrameworkStages, FrameworkStagesSearch } from "lib/types/graphQLEnums";
import { TenderStageV2Groups, TenderStageV2GroupsKeys } from "lib/types/models";
import FrameworkStage, { FRAMEWORK_STAGE_CONFIG } from "./FrameworkStage";

type Props = {
  onChange: (values: FrameworkStagesSearch[]) => void;
  checkedStages: FrameworkStagesSearch[];
};

const mapToSearch = (stage: FrameworkStages) => stage as unknown as FrameworkStagesSearch;
const mapFromSearch = (stage: FrameworkStagesSearch) => stage as unknown as FrameworkStages;

const grouped = Object.values(TenderStageV2Groups).map((group) => {
  const stages = Object.values(FRAMEWORK_STAGE_CONFIG)
    .filter((stage) => stage.group === group)
    .map((stage) => ({
      ...stage,
      id: mapToSearch(stage.id),
    }));
  return {
    id: group,
    name: group.replace("_", "-").toLowerCase(),
    stages,
  };
});

// Because this is used within Form.Item we have to wrap it in a forwardRef
const FrameworkStageButtons = React.forwardRef<HTMLDivElement, Props>(
  ({ onChange, checkedStages }, ref) => {
    const handleSelect = (stageId: FrameworkStagesSearch) => {
      if (checkedStages.includes(stageId)) {
        onChange(checkedStages.filter((v) => v !== stageId));
      } else {
        onChange([...checkedStages, stageId]);
      }
    };

    const handleGroupSelectAll = (groupId: TenderStageV2GroupsKeys) => {
      const stageIdsInGroup = Object.values(FRAMEWORK_STAGE_CONFIG)
        .filter((stage) => stage.group === groupId)
        .map((stage) => mapToSearch(stage.id));
      const stageIdsMissingInCurrentSelection = stageIdsInGroup.filter(
        (stageId) => !checkedStages.includes(stageId),
      );

      if (stageIdsMissingInCurrentSelection.length === 0) {
        onChange(checkedStages.filter((stage) => !stageIdsInGroup.includes(stage)));
      } else {
        onChange([...checkedStages, ...stageIdsMissingInCurrentSelection]);
      }
    };

    return (
      <Flex ref={ref} column gap="24px">
        {grouped.map((group, index) => (
          <Flex key={group.id} column gap="16px">
            {index !== 0 && <Divider margin="0" />}
            <Text color="sysTextBlack" textTransform="capitalize">
              {group.name}
            </Text>
            <Flex column gap="8px">
              <Flex alignItems="center" gap="8px">
                <Checkbox
                  checked={group.stages.every((stage) => checkedStages.includes(stage.id))}
                  onCheckedChange={() => handleGroupSelectAll(group.id)}
                />
                <Text onClick={() => handleGroupSelectAll(group.id)}>Select All</Text>
              </Flex>
              {group.stages.map((stage) => (
                <Flex key={stage.id} alignItems="center" gap="8px">
                  <Checkbox
                    checked={checkedStages.includes(stage.id)}
                    onCheckedChange={() => handleSelect(stage.id)}
                  />
                  <FrameworkStage
                    stage={mapFromSearch(stage.id)}
                    onClick={() => handleSelect(stage.id)}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  },
);

FrameworkStageButtons.displayName = "FrameworkStageButtons";

export default FrameworkStageButtons;
